import { getStoredCookieConsent, isAnalyticsEnabledByConsent } from '@/lib/cookie-consent';

const PLAUSIBLE_SCRIPT_PATH = '/js/script.js';
const PLAUSIBLE_DEFAULT_HOST = 'plausible.io';

type PlausibleWindow = typeof window & {
  plausible?: (
    event: string,
    options?: { props?: Record<string, string | number | boolean> }
  ) => void;
};

function getPlausibleHost(): string {
  const raw = (import.meta.env.VITE_PLAUSIBLE_HOST as string | undefined)?.trim();
  if (!raw) {
    return PLAUSIBLE_DEFAULT_HOST;
  }
  return raw.replace(/\/+$/, '');
}

function getPlausibleDomain(): string | null {
  const raw = (import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined)?.trim();
  return raw ? raw : null;
}

function ensureDnsPrefetch(host: string): void {
  const href = `https://${host}`;
  const existing = document.querySelector<HTMLLinkElement>(
    `link[rel="dns-prefetch"][href="${href}"]`
  );
  if (existing) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'dns-prefetch';
  link.href = href;
  document.head.appendChild(link);
}

let analyticsInitialized = false;

export function initAnalytics(): void {
  const domain = getPlausibleDomain();
  if (!domain) {
    return;
  }

  if (!isAnalyticsEnabledByConsent(getStoredCookieConsent())) {
    return;
  }

  if (analyticsInitialized) {
    return;
  }
  analyticsInitialized = true;

  const host = getPlausibleHost();
  const scriptSrc = `https://${host}${PLAUSIBLE_SCRIPT_PATH}`;

  ensureDnsPrefetch(host);

  if (
    document.querySelector<HTMLScriptElement>(`script[data-plausible][data-domain="${domain}"]`)
  ) {
    return;
  }

  const script = document.createElement('script');
  script.defer = true;
  script.dataset.plausible = 'true';
  script.dataset.domain = domain;
  script.src = scriptSrc;
  document.head.appendChild(script);
}

export function trackEvent(event: string, props?: Record<string, string | number | boolean>): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isAnalyticsEnabledByConsent(getStoredCookieConsent())) {
    return;
  }

  const plausible = (window as PlausibleWindow).plausible;
  if (typeof plausible !== 'function') {
    return;
  }
  if (props) {
    plausible(event, { props });
  } else {
    plausible(event);
  }
}

function resetAnalyticsState(): void {
  analyticsInitialized = false;
}

export const __testing = {
  getPlausibleHost,
  getPlausibleDomain,
  ensureDnsPrefetch,
  resetAnalyticsState,
};
